import { useEffect, useState } from "react";
import { reverse360 } from "@/api";
import { ArrowLeft, MessageCircle, TrendingUp, Quote } from "lucide-react";
import { Link } from "react-router-dom";

const DIM_COLORS = {
  communication:        "#0033AD",
  payments:             "#E4002B",
  fairness:             "#D4A437",
  innovation_openness:  "#7C3AED",
  operational_clarity:  "#009639",
  long_term_partnership:"#0EA5E9",
};

export default function Reverse360Admin() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await reverse360.summary(period ? { period } : {});
      setData(d);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]); // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="reverse360-admin-page">
      <Link to="/admin/configuracion" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver a configuración
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">feedback de proveedores</div>
          <h1 className="font-display text-4xl md:text-5xl">Cómo nos califican a Lala</h1>
          <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
            Reporte agregado y anónimo del 360° invertido. Los proveedores nos califican en 6 dimensiones.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Período</label>
          <input value={period} onChange={(e) => setPeriod(e.target.value)}
            className="input-field w-32" placeholder="2026-02 (vacío=todos)" data-testid="reverse-admin-period" />
        </div>
      </div>

      {loading || !data ? (
        <div className="text-center py-20 font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="reverse-summary-cards">
            <Stat label="Calificación global a Lala" value={data.overall_average?.toFixed?.(2) ?? "—"} sub="escala 0–10" accent="var(--lala-blue)" />
            <Stat label="Respuestas recibidas" value={data.responses_count} sub={period ? `período ${period}` : "todos los períodos"} />
            <Stat label="Comentarios abiertos" value={data.comments?.length || 0} sub="leídos al 100% por el equipo" accent="#D4A437" />
          </div>

          <section className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="reverse-dimensions-section">
            <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2"><TrendingUp size={18} /> dimensiones</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {data.dimensions.map((d) => (
                <div key={d.key} className="bg-[var(--cream)] border border-[var(--line)] rounded-xl p-4" data-testid={`reverse-dim-${d.key}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-sm font-semibold" style={{ color: DIM_COLORS[d.key] }}>{d.label}</div>
                    <div className="font-display text-3xl" style={{ color: DIM_COLORS[d.key] }}>
                      {d.average?.toFixed?.(2) ?? "—"}
                    </div>
                  </div>
                  <div className="h-2 mt-2 rounded-full bg-[var(--cream-deep)]">
                    <div className="h-2 rounded-full" style={{ width: `${((d.average || 0) / 10) * 100}%`, background: DIM_COLORS[d.key] }} />
                  </div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1">{d.responses} respuesta{d.responses === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="reverse-comments-section">
            <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2"><MessageCircle size={18} /> comentarios abiertos (anónimos)</div>
            <div className="space-y-3 mt-4">
              {(data.comments || []).length === 0 && (
                <div className="text-[var(--ink-soft)] text-sm italic py-6 text-center">Aún no hay comentarios para este período.</div>
              )}
              {(data.comments || []).map((c, i) => (
                <article key={i} className="bg-[var(--cream)] border border-[var(--line)] rounded-xl p-4" data-testid={`reverse-comment-${i}`}>
                  <div className="flex items-start gap-2 text-[var(--ink-soft)] text-xs uppercase tracking-wider font-semibold">
                    <Quote size={12} /> {c.supplier_category || "Sin categoría"} · {c.period}
                  </div>
                  <p className="mt-2 text-[var(--ink)] italic leading-relaxed">"{c.comment}"</p>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent = "var(--lala-blue)" }) {
  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-5">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</div>
      <div className="font-display text-4xl mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-[var(--ink-soft)] mt-1">{sub}</div>}
    </div>
  );
}
